'use client';

import { useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { isPublicPath } from './publicPaths';

/**
 * Tracks the last visited internal (non-public) paths in sessionStorage
 * and provides `goBack()` — navigates to the most recent path different
 * from the current one, or '/' when no in-app history exists.
 *
 * More reliable than router.back(): the browser history entry may point
 * outside the app (e.g. direct entry from Google). Pages that render
 * <UnauthorizedMessage /> mark their pathname via markPathUnauthorized() —
 * goBack() skips them together with the current page, so the user lands
 * on the last accessible page instead of bouncing between denied pages.
 *
 * Usage:
 *   // Layout.tsx — once per navigation
 *   useEffect(() => { if (pathname) recordPathVisit(pathname + window.location.search); }, [pathname]);
 *
 *   // Component
 *   const goBack = useReturnPath();
 *   <button onClick={goBack}>Zpět</button>
 */

const MAX_DEPTH = 20;

/** Per-user klíče — sdílený browser nesmí míchat historii dvou uživatelů. */
function scope(): string {
  try { return window.localStorage.getItem('userId') || 'anon'; } catch { return 'anon'; }
}
function stackKey() { return `armorik:pathStack:${scope()}`; }
function unauthorizedKey() { return `armorik:unauthorizedPaths:${scope()}`; }

function readStack(): string[] {
  try {
    const raw = sessionStorage.getItem(stackKey());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeStack(stack: string[]) {
  try {
    sessionStorage.setItem(stackKey(), JSON.stringify(stack.slice(-MAX_DEPTH)));
  } catch {}
}

function readUnauthorized(): Set<string> {
  try {
    const raw = sessionStorage.getItem(unauthorizedKey());
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

/** Marks a pathname as unauthorized — goBack() will skip it when navigating back. */
export function markPathUnauthorized(pathname: string) {
  if (typeof window === 'undefined' || !pathname) return;
  const set = readUnauthorized();
  if (set.has(pathname)) return;
  set.add(pathname);
  try {
    sessionStorage.setItem(unauthorizedKey(), JSON.stringify(Array.from(set).slice(-MAX_DEPTH * 2)));
  } catch {}
}

/** Records a visited internal path. Public/auth paths are never recorded. */
export function recordPathVisit(path: string) {
  if (typeof window === 'undefined') return;
  const pathname = path.split('?')[0];
  if (!pathname || isPublicPath(pathname)) return;
  const stack = readStack();
  if (stack[stack.length - 1] === path) return;
  stack.push(path);
  writeStack(stack);
}

/** Returns `goBack()` — navigates to the last accessible internal path ≠ current, else '/'. */
export function useReturnPath() {
  const router = useRouter();
  const pathname = usePathname();
  return useCallback(() => {
    const stack = readStack();
    const denied = readUnauthorized();
    while (
      stack.length &&
      (stack[stack.length - 1].split('?')[0] === pathname || denied.has(stack[stack.length - 1].split('?')[0]))
    ) stack.pop();
    const target = stack.pop();
    writeStack(stack);
    router.push(target ?? '/');
  }, [router, pathname]);
}
